import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Base, ReverLoading } from '@itsrever/design-system'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { seesEveryGroup } from '@/domain/store-group'
import NotFound from '@/ui/pages/404.page'
import { NavigationTabs, PageHeader } from '@/ui/shared'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'
import { Exchanges, ExchangesTab } from '@/ui/components/exchanges'
import {
  WarehousesScope,
  useWarehousesScope
} from '@/hooks/use-warehouses-scope'
import {
  useExchangesShown,
  useFullCatalogShown,
  useInstantExchangeShown
} from '@/hooks/use-scope-platform'
import { useScopePortals } from '@/application/queries'
import { useReturnsAndRefundsV2 } from '@/hooks/use-returns-and-refunds-v2'
import { TabProps } from '@/ui/shared'
import { useExchangesTabs, useRefundsTabs } from './configuration-tabs'

const { Tab, TabsIndicator, TabsList, TabsRoot } = Base

export default function ExchangesPage() {
  const { t } = useTranslation()
  const profile = useAppSelector((s) => s.userApi.getMe)
  // getMe is dispatched from an effect: deciding before it lands would flash the 404 at an admin.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = profile.response.user?.role === Roles.ReadWrite
  const { scope, showGroup, showStore } = useWarehousesScope()
  const tabs = useExchangesTabs()
  const refundTabs = useRefundsTabs()
  const { refunds: withRefunds, loading: loadingV2 } = useReturnsAndRefundsV2()
  const exchangesShown = useExchangesShown()
  const fullCatalogShown = useFullCatalogShown()
  const instantShown = useInstantExchangeShown()
  const { loadingPortals } = useScopePortals()
  const { pathname } = useLocation()
  const tab: ExchangesTab = pathname.endsWith('/orders')
    ? 'orders'
    : pathname.endsWith('/full-catalog')
      ? 'catalog'
      : pathname.endsWith('/instant-exchange')
        ? 'instant'
        : 'general'

  // Until the refunds flag answers, the page can't tell which tab bar it has.
  if (loadingProfile || loadingV2) return <ReverLoading />
  if (!isAdmin) return <NotFound />
  if (tab === 'instant' && !seesEveryGroup(profile.response.user))
    return <NotFound />
  // The group view knows its platforms only once the portal list loads.
  if (
    !exchangesShown ||
    (tab === 'catalog' && !fullCatalogShown) ||
    (tab === 'instant' && !instantShown)
  )
    return loadingPortals ? <ReverLoading /> : <NotFound />
  const path = pathname.replace(/\/$/, '')
  return (
    <>
      {withRefunds ? (
        <>
          <NavigationTabs
            tabs={refundTabs}
            selectedIndex={refundTabs.findIndex(
              (item) => item.link === '/configuration/exchanges'
            )}
          />
          <SubTabs tabs={tabs} value={path} />
        </>
      ) : (
        <NavigationTabs
          tabs={tabs}
          selectedIndex={tabs.findIndex((item) => item.link === path)}
        />
      )}
      {scope.kind === 'none' ? (
        <>
          <PageHeader title={t('exchanges.title')} />
          <p className="px-7 py-6 text-sm text-neutral-600">
            {t('exchanges.no_scope')}
          </p>
        </>
      ) : (
        <GroupExchanges
          key={scope.group}
          scope={scope}
          tab={tab}
          onShowGroup={showGroup}
          onShowStore={showStore}
        />
      )}
    </>
  )
}

function GroupExchanges({
  scope,
  tab,
  onShowGroup,
  onShowStore
}: {
  scope: Exclude<WarehousesScope, { kind: 'none' }>
  tab: ExchangesTab
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}) {
  const { isFlagEnabled: blocked } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })
  return (
    <>
      {blocked && <BlockedDashboardOverlay />}
      <Exchanges
        scope={scope}
        tab={tab}
        onShowGroup={onShowGroup}
        onShowStore={onShowStore}
      />
    </>
  )
}

function SubTabs({ tabs, value }: { tabs: TabProps[]; value: string }) {
  const navigate = useNavigate()
  const { search } = useLocation()
  return (
    <div className="px-7 pb-1 pt-4">
      <TabsRoot
        value={value}
        onValueChange={(link) => navigate(`${link}${search}`)}
      >
        <TabsList variant="segmented">
          {tabs.map((tab) => (
            <Tab key={tab.link} value={tab.link}>
              {tab.copy}
              {tab.badge ? (
                <Base.Badge tone="info">{tab.badge}</Base.Badge>
              ) : null}
            </Tab>
          ))}
          <TabsIndicator />
        </TabsList>
      </TabsRoot>
    </div>
  )
}
